import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

type Breakpoint = "sm" | "md" | "lg"

const DESKTOP_VIS: Record<Breakpoint, string> = {
  sm: "hidden sm:block",
  md: "hidden md:block",
  lg: "hidden lg:block",
}

const MOBILE_VIS: Record<Breakpoint, string> = {
  sm: "sm:hidden",
  md: "md:hidden",
  lg: "lg:hidden",
}

interface RootProps {
  /** Breakpoint where mobile-to-desktop handoff happens. Default `md` (768px). */
  breakpoint?: Breakpoint
  /** Wrapper className applied to the outer container. */
  className?: string
  children: ReactNode
}

interface SlotProps {
  /** Override the parent breakpoint for this slot specifically. */
  breakpoint?: Breakpoint
  /** Add overflow-x-auto + scroll-hint-fade so the table can scroll within the slot. */
  scroll?: boolean
  /** Combined with `scroll`, extends the scroll area edge-to-edge on phones. */
  bleed?: boolean
  className?: string
  children: ReactNode
}

/**
 * Two-slot responsive table primitive. Codifies the desktop-table plus
 * mobile-card-list pattern that recurs across the codebase.
 */
function ResponsiveTableRoot({ breakpoint = "md", className, children }: RootProps) {
  return (
    <div data-responsive-table data-breakpoint={breakpoint} className={className}>
      {children}
    </div>
  )
}

function Desktop({ breakpoint = "md", scroll = false, bleed = false, className, children }: SlotProps) {
  return (
    <div
      className={cn(
        DESKTOP_VIS[breakpoint],
        scroll && "overflow-x-auto scroll-hint-fade",
        scroll && bleed && "-mx-4 px-4",
        className
      )}
    >
      {children}
    </div>
  )
}

function Mobile({ breakpoint = "md", scroll = false, bleed = false, className, children }: SlotProps) {
  return (
    <div
      className={cn(
        MOBILE_VIS[breakpoint],
        scroll && "overflow-x-auto scroll-hint-fade",
        scroll && bleed && "-mx-4 px-4",
        className
      )}
    >
      {children}
    </div>
  )
}

export const ResponsiveTable = Object.assign(ResponsiveTableRoot, {
  Desktop,
  Mobile,
})
